// The five areas of the rail (docs/UI-POLISH.md decision 4) and the pages each one's panel lists. The pages themselves
// (`controlItems`, `liveOpsItems`) and the URL builders stay in routes.ts; this file only groups them and gives them icons.
import { Box, CalendarDays, Database, Dices, FlaskConical, Gift, GitMerge, History, Image, Languages, Layers, Package, Percent, Puzzle, Receipt, Rocket, Search, Server, Settings, ShoppingBag, SlidersHorizontal, Star, Store, Target, Users, Zap, type LucideIcon } from '../ui/icons';
import { controlItems, liveOpsItems, routes, type SidebarItem } from './routes';

export type AreaKey = 'content' | 'liveops' | 'players' | 'releases' | 'settings';

export interface Area {
  key: AreaKey;
  label: string;
  icon: LucideIcon;
  items: SidebarItem[];
}

/** the icon of a sidebar item, by its key */
export const itemIcons: Record<string, LucideIcon> = {
  history: History,
  releases: Rocket,
  'merge-requests': GitMerge,
  templates: Database,
  localization: Languages,
  assets: Image,
  products: ShoppingBag,
  extensions: Puzzle,
  platforms: Server,
  'env-settings': SlidersHorizontal,
  players: Users,
  orders: Receipt,
  settings: Settings,
  calendar: CalendarDays,
  events: Zap,
  offers: Gift,
  store: Store,
  items: Box,
  'store-items': Package,
  'daily-bonus': Star,
  segments: Target,
  overrides: Layers,
  'ab-tests': FlaskConical,
  'reward-tables': Dices,
  sales: Percent,
  search: Search,
};

const pick = (keys: string[]) => keys.map((k) => controlItems.find((i) => i.key === k)).filter((i): i is SidebarItem => !!i);

export const areas: Area[] = [
  { key: 'content', label: 'Content', icon: Database, items: pick(['templates', 'localization', 'assets', 'products', 'extensions']) },
  { key: 'liveops', label: 'LiveOps', icon: Zap, items: liveOpsItems },
  { key: 'players', label: 'Players', icon: Users, items: pick(['players', 'orders']) },
  { key: 'releases', label: 'Releases', icon: Rocket, items: pick(['history', 'merge-requests', 'releases']) },
  { key: 'settings', label: 'Settings', icon: Settings, items: pick(['settings', 'platforms', 'env-settings']) },
];

export function areaByKey(key: AreaKey): Area {
  return areas.find((a) => a.key === key)!;
}

/**
 * The area a page belongs to, from the page segment of its URL (the part after `branches/{branch}/`, `envs/{env}/` or
 * `games/{game}/`) and which of the three it sits under. The game home has no area.
 */
export function areaOf(segment: string, scope: 'branch' | 'env' | 'game'): AreaKey | null {
  const first = segment.split('/')[0] ?? '';
  if (scope === 'env') return first === 'players' || first === 'orders' ? 'players' : 'settings';
  if (scope === 'game') return first === 'settings' ? 'settings' : null;
  if (first === 'liveops') return 'liveops';
  if (first === 'history' || first === 'merge-requests' || first === 'releases') return 'releases';
  return 'content';
}

/** The URL of a sidebar item in the game on screen: the branch for branch pages, the environment for environment pages. */
export function itemHref(item: SidebarItem, ctx: { org: string; game: string; branch: string; env: string }): string {
  if (item.scope === 'branch') return routes.branch(ctx.org, ctx.game, ctx.branch, item.page);
  if (item.scope === 'env') return routes.env(ctx.org, ctx.game, ctx.env, item.page);
  return routes.gamePage(ctx.org, ctx.game, item.page);
}
